import { Injectable } from '@angular/core';
import { Login, LoginResponse, User } from '@se/core/models';
import { Observable, BehaviorSubject } from 'rxjs';
import { REST_BASE_URL, REST_URLS, WEB_STORAGE_KEYS, APP_ROUTE } from '@se/core/constants';
import { ApiService } from '../_helpers/api.service';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { WebStorageService } from '../utility';
import { LeadsService } from './leads.service';

declare global {
  interface Window { dataLayer: any[]; fby: any; ga: any }
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  public openedFromHO = false;
  public showWelcomeScreen = true;
  private currentUserSubject: BehaviorSubject<LoginResponse>;
  public ISRUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<LoginResponse>;
  public ISRUser: Observable<any>;
  public loadNewLoginDialog: BehaviorSubject<any>;
  public webPSupported = false;

  // store the URL so we can redirect after logging in
  public redirectUrl: string;

  private getCurrentAuthScope(): 'v3' | 'v4' | 'other' {
    const href = window.location.href;
    if (href.includes('/v3/nl-nl')) {
      return 'v4';
    }
    if (href.includes('/v4')) {
      return 'v4';
    }
    if (href.includes('/v3') && !href.includes('/v3/nl-nl')) {
      return 'v3';
    }
    return 'other';
  }

  private getAuthStorageKey(scope = this.getCurrentAuthScope()): string {
    if (scope === 'v3') {
      return WEB_STORAGE_KEYS.auth_v3;
    }
    if (scope === 'v4') {
      return WEB_STORAGE_KEYS.auth_v4;
    }
    return WEB_STORAGE_KEYS.auth;
  }

  private parseAuth(rawAuth: string): LoginResponse | null {
    if (!rawAuth) {
      return null;
    }
    try {
      return JSON.parse(rawAuth);
    } catch (e) {
      return null;
    }
  }

  private getScopedAuth(scope = this.getCurrentAuthScope(), allowLegacy = false): LoginResponse | null {
    const scopedAuth = this.parseAuth(this._webStorage.get(this.getAuthStorageKey(scope)));
    if (scopedAuth) {
      return scopedAuth;
    }
    if (allowLegacy) {
      return this.parseAuth(this._webStorage.get(WEB_STORAGE_KEYS.auth));
    }
    return null;
  }

  private setScopedAuth(auth: LoginResponse | null, scope = this.getCurrentAuthScope()) {
    const key = this.getAuthStorageKey(scope);
    if (auth) {
      this._webStorage.set(key, JSON.stringify(auth));
    } else {
      this._webStorage.destroy(key);
    }
  }

  private enforceAuthScope() {
    const currentScope = this.getCurrentAuthScope();
    const scopedStorageKey = this.getAuthStorageKey(currentScope);
    const hasScopedAuth = Boolean(this._webStorage.get(scopedStorageKey));
    if (!hasScopedAuth) {
      const legacyAuth = this.parseAuth(this._webStorage.get(WEB_STORAGE_KEYS.auth));
      if (legacyAuth) {
        this.setScopedAuth(legacyAuth, currentScope);
        this._webStorage.destroy(WEB_STORAGE_KEYS.auth);
      }
    }
    const scopedAuth = this.getScopedAuth(currentScope, false);
    this.currentUserSubject.next(scopedAuth);
  }

  constructor(private _apiService: ApiService, private _router: Router, private _webStorage: WebStorageService, private _lead: LeadsService) {
    this.currentUserSubject = new BehaviorSubject<LoginResponse>(this.getScopedAuth());
    this.currentUser = this.currentUserSubject.asObservable();
    this.ISRUserSubject = new BehaviorSubject<any>(_webStorage.get(WEB_STORAGE_KEYS.enlighten_admin) || null);
    this.ISRUser = this.ISRUserSubject.asObservable();
    this.loadNewLoginDialog = new BehaviorSubject(false);

    const elem = document.createElement('canvas');
    if (!!(elem.getContext && elem.getContext('2d'))) {
      this.webPSupported = elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    }
    this.enforceAuthScope();
  }

  public get currentUserValue(): LoginResponse {
    return this.currentUserSubject.value;
  }
  public get isUserLoggedIn(): boolean {
    this.enforceAuthScope();
    const currentUser = this.currentUserValue;
    if (currentUser && currentUser.token) {
      return true;
    }
    return false;
  }
  setToken(token, authdata) {
    const auth = new LoginResponse({ token, authdata });
    this.setScopedAuth(auth);
    this.currentUserSubject.next(auth);
  }
  login(login: Login): Observable<LoginResponse> {
    return this._apiService.post(REST_BASE_URL.B2C, this._apiService.composeUrl([REST_URLS.POST.LOGIN]), login)
      .pipe(map(user => {
        // store user details and basic auth credentials in local storage to keep user logged in between page refreshes
        user.authdata = (login.username);
        this.setScopedAuth(user);
        this.currentUserSubject.next(user);
        return user;
      }));
  }
  logout(enligthenAdmin = false) {
    // remove user from local storage to log user out
    this.setScopedAuth(null);
    this.currentUserSubject.next(null);
    if (!enligthenAdmin) {  // If user is enligthen manager do not navigate to login page
      let URL = APP_ROUTE.LOGIN;
      if (window.location.href.includes('pse-us')) {
        URL = 'pse-us';
      } else if (this._lead.isV3) {
        URL = APP_ROUTE.V3;
      } else if (this._lead.isNLUser) {
        URL = APP_ROUTE.NLV4;
      } else if (this._lead.isV4) {
        URL = APP_ROUTE.V4;
      }

      this._router.navigate([URL]).then(() => {
        if (URL !== APP_ROUTE.LOGIN) {
          location.reload();
        }
      });
    } else {
      this.ISRUserSubject.next('');
      this._webStorage.destroy(WEB_STORAGE_KEYS.enlighten_admin);
    }
  }
}
